import React, { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Form, InputGroup, ListGroupItem } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import * as yup from 'yup'

import { IRegistration } from '../../../store/registrations/interfaces'
import { updateRegistration } from '../../../store/registrations/registrations'
import { AddValidatorFunc } from '../../../hooks/validators'

const FamilyData = (props: {
  registration: IRegistration
  editable: boolean
  setError: (prevState: boolean) => void
  addValidator?: AddValidatorFunc
}) => {
  const dispatch = useDispatch()
  const { registration, editable, setError, addValidator } = props
  const minChildren: number = registration.event.is_only_for_adults ? 0 : 1
  const maxChildren: number = registration.event.is_only_for_adults ? 0 : 10
  const { register, trigger, getValues, formState: { errors }, reset } = useForm<{
    // Note that these should be number types but with the way react-hook-form
    // handles number inputs it will yield strings... 😑
    numberOfAdults: string
    numberOfChildren: string
  }>({
    mode: 'onChange',
    defaultValues: {
      numberOfAdults: JSON.stringify(registration.numberOfAdults),
      numberOfChildren: JSON.stringify(registration.numberOfChildren)
    },
    resolver: yupResolver(yup.object().shape({
      numberOfAdults: yup
        .number()
        .max(2, 'Es können maximal 2 Erwachsene angemeldet werden.')
        .min(1, 'Es muss mindestens ein Erwachsener angemeldet werden.')
        .typeError('Dieses Feld ist zwingend erforderlich.'),
      numberOfChildren: yup
        .number()
        .min(minChildren, 'Es muss mindestens ein Kind angemeldet werden.')
        .max(
          maxChildren,
          'Es dürfen maximal ' + maxChildren + ' Kinder angemeldet werden',
        )
        .typeError('Dieses Feld ist zwingend erforderlich.'),
    })),
  })

  const lastAdultError = useRef(errors.numberOfAdults)
  const lastChildError = useRef(errors.numberOfChildren)

  useEffect(
      () => {
        const numberOfAdults = registration.numberOfAdults ? registration.numberOfAdults : 1
        const numberOfChildren = registration.numberOfChildren ? registration.numberOfChildren : registration.event.is_only_for_adults ? 0 : 1
        reset({
          numberOfAdults: numberOfAdults.toString(),
          numberOfChildren: numberOfChildren.toString()
        })
        console.table(getValues())
        dispatch(
          updateRegistration({
            eventId: registration.event.event_id,
            numberOfAdults: numberOfAdults,
            numberOfChildren: numberOfChildren,
          }),
        )
      }, []
  )

  useEffect(
    () => {
      if (
        lastAdultError.current === errors.numberOfAdults &&
        lastChildError.current === errors.numberOfChildren
      ) {
        if (editable && addValidator) {
          addValidator(`familyData-${registration.event.event_id}`, { trigger, formName: 'FamilyData' })
        }
      } else {
        if (errors.numberOfAdults) {
          setError(true)
          lastAdultError.current = errors.numberOfAdults
        } else if (errors.numberOfChildren) {
          setError(true)
          lastChildError.current = errors.numberOfChildren
        } else {
          setError(false)
          lastAdultError.current = errors.numberOfAdults
          lastChildError.current = errors.numberOfChildren
        }
      }
    },
    [register, errors.numberOfChildren, errors.numberOfAdults])

  return (
    <ListGroupItem className='m-0'>
      <Form.Group>
        <Form.Label>Familienanmeldung</Form.Label>
        <InputGroup className='mb-3'>
          <InputGroup.Prepend>
            <InputGroup.Text
              id={`numberOfAdults-${registration.event.event_id}-label`}
            >
              Erwachsene
            </InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            {...register('numberOfAdults', {
              onChange: () => {
                const { numberOfAdults } = getValues()
                dispatch(
                  updateRegistration({
                    eventId: registration.event.event_id,
                    numberOfAdults: parseInt(numberOfAdults || '1'),
                  }),
                )
              },
            })}
            id={`numberOfAdults-${registration.event.event_id}`}
            aria-labelledby={`numberOfAdults-${registration.event.event_id}-label`}
            type='number'
            disabled={!editable}
            style={{ textAlign: 'right' }}
            min='1'
            max='2'
            step='1'
            isInvalid={!!errors.numberOfAdults}
            value={registration.numberOfAdults.toString()}
          />
          <Form.Control.Feedback type='invalid'>
            {errors.numberOfAdults && errors.numberOfAdults.message}
          </Form.Control.Feedback>
        </InputGroup>
        <InputGroup>
          <InputGroup.Prepend>
            <InputGroup.Text
              id={`numberOfChildren-${registration.event.event_id}-label`}
            >
              Kinder
            </InputGroup.Text>
          </InputGroup.Prepend>
          <Form.Control
            {...register('numberOfChildren', {
              onChange: () => {
                const { numberOfChildren } = getValues()
                dispatch(
                  updateRegistration({
                    eventId: registration.event.event_id,
                    numberOfChildren: parseInt(numberOfChildren || '1'),
                  }),
                )
              },
            })}
            name='numberOfChildren'
            id={`numberOfChildren-${registration.event.event_id}`}
            aria-labelledby={`numberOfChildren-${registration.event.event_id}-label`}
            type='number'
            disabled={!editable || registration.event.is_only_for_adults}
            style={{ textAlign: 'right' }}
            min='1'
            max='10'
            step='1'
            isInvalid={!!errors.numberOfChildren}
            value={registration.event.is_only_for_adults ? "0" : Math.max(registration.numberOfChildren, 1).toString()}
          />
          <Form.Control.Feedback type='invalid'>
            {errors.numberOfChildren && errors.numberOfChildren.message}
          </Form.Control.Feedback>
        </InputGroup>
      </Form.Group>
    </ListGroupItem>
  )
}

export default FamilyData
